import { useEffect, useRef, useState } from "react";
import { API_URL, BASE_URL } from "../../config";
import "./news-admin.css";

const emptyNews = () => ({
    _id: null,
    label_ru: "", label_uz: "",
    title_ru: "", title_uz: "",
    html_ru: "", html_uz: "",
    images: [],
    videoUrl: null,
    articleType: "internal",
    articleUrl: "",
    newImages: [], // File objects
    newVideo: null // File object
});

export default function NewsAdmin() {
    const [form, setForm] = useState(emptyNews());
    const [items, setItems] = useState([]);
    const [error, setError] = useState("");
    const [langTab, setLangTab] = useState('ru'); 
    const editorRef = useRef(null);

    const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

    const loadNews = async () => {
        try {
            const res = await fetch(`${API_URL}/news`);
            const data = await res.json();
            setItems(data);
        } catch (err) {
            console.error("Ошибка загрузки новостей:", err);
        }
    };

    useEffect(() => {
        loadNews();
    }, []);

    const handleTabChange = (newLang) => {
        if (editorRef.current) {
            // Обязательно сохраняем текст в переменную до вызова setForm (так как setForm может выполниться асинхронно после изменения редактора)
            const currentHTML = editorRef.current.innerHTML;
            setForm(prev => ({
                ...prev,
                [langTab === 'ru' ? 'html_ru' : 'html_uz']: currentHTML
            }));
            const newHtml = newLang === 'ru' ? form.html_ru : form.html_uz;
            editorRef.current.innerHTML = newHtml;
        }
        setLangTab(newLang);
    };

    const handleFormChange = (field_ru, field_uz, value) => {
        setForm(prev => ({
            ...prev,
            [langTab === 'ru' ? field_ru : field_uz]: value
        }));
    };

    const onPickImages = (e) => {
        setError("");
        const files = Array.from(e.target.files || []);
        
        const currentTotal = form.images.length + form.newImages.length;
        if (files.length + currentTotal > 10) {
            setError("Можно загрузить максимум 10 изображений.");
            return;
        }

        const validFiles = [];
        for (const f of files) {
            if (!f.type.startsWith("image/")) {
                setError("Разрешены только изображения.");
                return;
            }
            if (f.size > 5 * 1024 * 1024) {
                setError(`Файл "${f.name}" больше 5MB.`);
                return;
            }
            validFiles.push(f);
        }

        if (validFiles.length) {
            setForm(s => ({ ...s, newImages: [...s.newImages, ...validFiles] }));
        }
        e.target.value = "";
    };

    const removeExistingImg = (idx) => {
        setForm(s => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
    };
    
    const removeNewImg = (idx) => {
        setForm(s => ({ ...s, newImages: s.newImages.filter((_, i) => i !== idx) }));
    };

    const onPickVideo = (e) => {
        setError("");
        const file = (e.target.files || [])[0];
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            setError("Разрешены только видеофайлы.");
            e.target.value = "";
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            setError("Видео больше 15MB. Выберите файл поменьше.");
            e.target.value = "";
            return;
        }

        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
        setForm(s => ({ ...s, newVideo: file }));
        setVideoPreviewUrl(URL.createObjectURL(file));
        e.target.value = "";
    };

    const clearVideo = () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
        setForm(s => ({ ...s, newVideo: null, videoUrl: null }));
        setVideoPreviewUrl(null);
    };

    const startEdit = (doc) => {
        const resetHtml = { html_ru: doc.html_ru || "", html_uz: doc.html_uz || "" };
        setForm({
            _id: doc._id,
            label_ru: doc.label_ru || "", label_uz: doc.label_uz || "",
            title_ru: doc.title_ru || "", title_uz: doc.title_uz || "",
            ...resetHtml,
            images: doc.images || [],
            videoUrl: doc.videoUrl || null,
            articleType: doc.articleType || "internal",
            articleUrl: doc.articleUrl || "",
            newImages: [],
            newVideo: null
        });
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
        setVideoPreviewUrl(null);
        
        if (editorRef.current) {
            editorRef.current.innerHTML = langTab === 'ru' ? resetHtml.html_ru : resetHtml.html_uz;
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");

        const currentHtml = editorRef.current?.innerHTML?.trim() || "";
        const html_ru = langTab === 'ru' ? currentHtml : form.html_ru;
        const html_uz = langTab === 'uz' ? currentHtml : form.html_uz;

        if (!form.title_ru.trim() && !form.title_uz.trim()) return setError("Заполните заголовок хотя бы на одном языке.");
        if (!html_ru && !html_uz) return setError("Добавьте текст статьи хотя бы на одном языке.");
        if (form.articleType === "external" && !isValidUrl(form.articleUrl)) {
            return setError("Укажите корректный URL для внешней статьи.");
        }

        const fd = new FormData();
        fd.append('label_ru', form.label_ru);
        fd.append('label_uz', form.label_uz);
        fd.append('title_ru', form.title_ru);
        fd.append('title_uz', form.title_uz);
        fd.append('html_ru', html_ru);
        fd.append('html_uz', html_uz);
        fd.append('articleType', form.articleType);
        fd.append('articleUrl', form.articleUrl);

        if (form.newVideo) {
            fd.append('video', form.newVideo);
        } else if (form.videoUrl) {
            fd.append('videoUrl', form.videoUrl); // keep existing
        }

        if (form._id) {
            fd.append('existingImages', JSON.stringify(form.images));
            form.newImages.forEach(f => fd.append('newImages', f));
            try {
                const res = await fetch(`${API_URL}/news/${form._id}`, { method: 'PUT', body: fd });
                if (res.ok) resetForm();
            } catch (err) { setError(err.message); }
        } else {
            form.newImages.forEach(f => fd.append('images', f));
            try {
                const res = await fetch(`${API_URL}/news`, { method: 'POST', body: fd });
                if (res.ok) resetForm();
            } catch (err) { setError(err.message); }
        }
        loadNews();
    };

    const resetForm = () => {
        setForm(emptyNews());
        if (editorRef.current) editorRef.current.innerHTML = "";
        clearVideo();
    };

    const deleteItem = async (id) => {
        if (!window.confirm("Удалить новость?")) return;
        try {
            await fetch(`${API_URL}/news/${id}`, { method: 'DELETE' });
            if (form._id === id) resetForm();
            loadNews();
        } catch (e) { alert(e.message); }
    };

    return (
        <div className="news-admin">
            <h1>Новости — админ</h1>
            <form className="news-form" onSubmit={onSubmit}>
                {error && <div className="news-error">{error}</div>}

                <div style={{display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                    <button 
                        type="button"
                        className={`btn ${langTab === 'ru' ? 'btn-primary' : 'btn-outline'}`} 
                        onClick={() => handleTabChange('ru')}
                    >
                        Русский
                    </button>
                    <button 
                        type="button"
                        className={`btn ${langTab === 'uz' ? 'btn-primary' : 'btn-outline'}`} 
                        onClick={() => handleTabChange('uz')}
                    >
                        O'zbekcha
                    </button>
                </div>

                <div className="nf-row">
                    <label>
                        <div className="nf-label">Тег ({langTab.toUpperCase()})</div>
                        <input
                            type="text"
                            placeholder="Например: В СМИ о нас"
                            value={langTab === 'ru' ? form.label_ru : form.label_uz}
                            onChange={(e) => handleFormChange('label_ru', 'label_uz', e.target.value)}
                        />
                    </label>

                    <label>
                        <div className="nf-label">Заголовок ({langTab.toUpperCase()})</div>
                        <input
                            type="text"
                            placeholder="Заголовок новости"
                            value={langTab === 'ru' ? form.title_ru : form.title_uz}
                            onChange={(e) => handleFormChange('title_ru', 'title_uz', e.target.value)}
                        />
                    </label>
                </div>

                <div className="nf-block">
                    <div className="nf-label">Текст ({langTab.toUpperCase()})</div>

                    <div className="nf-toolbar">
                        <button type="button" onClick={() => exec("bold")}><b>B</b></button>
                        <button type="button" onClick={() => exec("italic")}><i>I</i></button>
                        <button type="button" onClick={() => exec("underline")}><u>U</u></button>
                        <select onChange={(e) => exec("fontSize", false, e.target.value)} defaultValue="3">
                            <option value="2">Мелкий</option>
                            <option value="3">Обычный</option>
                            <option value="4">Крупный</option>
                            <option value="5">Очень крупный</option>
                        </select>
                        <button type="button" onClick={() => exec("insertUnorderedList")}>• List</button>
                        <button type="button" onClick={() => exec("justifyLeft")}>⟸</button>
                        <button type="button" onClick={() => exec("justifyCenter")}>⇔</button>
                        <button type="button" onClick={() => exec("justifyRight")}>⟹</button>
                    </div>

                    <div
                        ref={editorRef}
                        className="nf-editor"
                        contentEditable
                        suppressContentEditableWarning
                        placeholder="Введите текст новости…"
                    />
                    <div className="nf-hint">Поддерживаются: жирный, курсив, размер шрифта, списки, выравнивание.</div>
                </div>

                <div className="nf-block">
                    <div className="nf-label">Фотографии (до 10, ≤5MB каждая)</div>
                    <input type="file" multiple accept="image/*" onChange={onPickImages} />
                    <div className="nf-previews">
                        {form.images.map((src, i) => (
                            <div key={`ex-${i}`} className="nf-thumb">
                                <img src={`${BASE_URL}${src}`} alt={"img-" + i} />
                                <button type="button" className="nf-del" onClick={() => removeExistingImg(i)}>×</button>
                            </div>
                        ))}
                        {form.newImages.map((file, i) => (
                            <div key={`new-${i}`} className="nf-thumb">
                                <img src={URL.createObjectURL(file)} alt={"img-" + i} />
                                <button type="button" className="nf-del" onClick={() => removeNewImg(i)}>×</button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="nf-block">
                    <div className="nf-label">Видео (1 файл, ≤15MB)</div>
                    <input type="file" accept="video/*" onChange={onPickVideo} />
                    
                    {videoPreviewUrl ? (
                         <div className="nf-video-preview">
                         <video src={videoPreviewUrl} controls playsInline style={{ width: "100%", maxHeight: 260 }} />
                         <button type="button" className="nf-del nf-del-video" onClick={clearVideo}>Удалить видео</button>
                     </div>
                    ) : form.videoUrl ? (
                        <div className="nf-video-preview">
                            <video src={`${BASE_URL}${form.videoUrl}`} controls playsInline style={{ width: "100%", maxHeight: 260 }} />
                            <button type="button" className="nf-del nf-del-video" onClick={clearVideo}>Удалить видео</button>
                        </div>
                    ) : (
                        <div className="nf-hint">Если загрузить видео, оно будет показано в модалке статьи.</div>
                    )}
                </div>

                <div className="nf-row">
                    <div className="nf-block">
                        <div className="nf-label">Тип статьи</div>
                        <label className="radio">
                            <input
                                type="radio"
                                name="atype"
                                checked={form.articleType === "internal"}
                                onChange={() => setForm({ ...form, articleType: "internal", articleUrl: "" })}
                            />
                            <span>Личная для сайта (модалка)</span>
                        </label>
                        <label className="radio">
                            <input
                                type="radio"
                                name="atype"
                                checked={form.articleType === "external"}
                                onChange={() => setForm({ ...form, articleType: "external" })}
                            />
                            <span>В соцсетях / внешняя ссылка</span>
                        </label>
                    </div>

                    {form.articleType === "external" && (
                        <label className="nf-block" style={{ flex: 1 }}>
                            <div className="nf-label">URL статьи</div>
                            <input
                                type="url"
                                placeholder="https://…"
                                value={form.articleUrl}
                                onChange={(e) => setForm({ ...form, articleUrl: e.target.value })}
                            />
                        </label>
                    )}
                </div>

                <div className="nf-actions">
                    <button type="submit" className="nf-submit">{form._id ? "Сохранить изменения" : "Добавить новость"}</button>
                    <button type="button" className="nf-reset" onClick={resetForm}>Сбросить</button>
                </div>
            </form>

            <h2 style={{ marginTop: 28 }}>Сохранённые новости</h2>
            <ul className="nf-list">
                {items.map((n) => (
                    <li key={n._id} className="nf-item">
                        <div>
                            <div className="nf-item-title">{n.title_ru || n.title}</div>
                            <div className="nf-item-sub">
                                {n.label_ru || n.label || "—"} · {new Date(n.createdAt).toLocaleString()}
                                {n.videoUrl ? " · есть видео" : ""}
                            </div>
                        </div>
                        <div>
                           <button className="btn btn-outline" style={{marginRight: '10px'}} onClick={() => startEdit(n)}>Редактировать</button>
                           <button className="nf-del-btn" onClick={() => deleteItem(n._id)}>Удалить</button>
                        </div>
                    </li>
                ))}
                {items.length === 0 && <li className="nf-empty">Нет новостей</li>}
            </ul>
        </div>
    );
}

function exec(cmd, ui = false, value = null) {
    document.execCommand(cmd, ui, value);
}

function isValidUrl(u) {
    try { new URL(u); return true; } catch { return false; }
}
